import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Truck, User, Loader2, AlertTriangle, Clock, Zap, CheckCircle, Building2, Star } from 'lucide-react';
import { formatEta } from '../lib/date';



// Timer Component
const MissionTimer = ({ startTime, isActive }) => {
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    if (!isActive || !startTime) return;
    
    const interval = setInterval(() => {
      const now = Date.now();
      const start = new Date(startTime).getTime();
      setElapsed(Math.floor((now - start) / 1000));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [startTime, isActive]);
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  if (!isActive) return null;
  
  return (
    <div className="flex items-center justify-center gap-4 p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl text-white">
      <div className="relative">
        <div className="w-20 h-20 rounded-full border-4 border-white/20 flex items-center justify-center timer-pulse">
          <Clock className="w-8 h-8 text-white" />
        </div>
      </div>
      <div>
        <p className="text-sm text-slate-300 mb-1">Tempo decorrido</p>
        <p className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'JetBrains Mono' }}>
          {formatTime(elapsed)}
        </p>
        <p className="text-xs text-slate-400 mt-1">Aguardando resposta dos fornecedores</p>
      </div>
    </div>
  );
};

// Supplier Response Card with SLA Performance
const SupplierResponseCard = ({ supplier, onSelect, isSelected, responseTime }) => {
  // Simular SLA baseado no ID do supplier para ter valores consistentes
  const slaPercent = 85 + (supplier.id?.charCodeAt?.(0) % 15 || Math.floor(Math.random() * 15));
  const slaColor = slaPercent >= 95 ? 'text-green-600 bg-green-50' : 
                   slaPercent >= 85 ? 'text-amber-600 bg-amber-50' : 
                   'text-red-600 bg-red-50';
  
  return (
    <div 
      className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
        isSelected 
          ? 'border-slate-900 bg-slate-50 shadow-lg' 
          : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
      }`}
      onClick={() => onSelect(supplier)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSelected ? 'bg-slate-900 text-white' : 'bg-slate-100'}`}>
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <p className="font-medium text-slate-900">{supplier.name}</p>
            <p className="text-sm text-slate-500">{supplier.city || 'São Paulo'}, {supplier.state || 'SP'}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-slate-400">Tempo estimado</p>
          <p className="font-semibold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
            {supplier.pricing?.[0]?.estimated_arrival_minutes || 30} min
          </p>
        </div>
      </div>
      
      {/* SLA Performance Badge */}
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge className={`${slaColor} border-0 rounded-full`}>
            SLA: {slaPercent}%
          </Badge>
          {responseTime && (
            <Badge className="bg-green-50 text-green-600 border border-green-100 rounded-full">
              Respondeu em {responseTime}s
            </Badge>
          )}
        </div>
        {supplier.pricing?.[0]?.base_price && (
          <span className="text-sm text-slate-500">
            Base: <span className="font-mono font-medium text-slate-700">R$ {supplier.pricing[0].base_price}</span>
          </span>
        )}
      </div>
    </div>
  );
};

const NewMissionPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [step, setStep] = useState(1); // 1: Form, 2: Select Suppliers to Notify, 3: Waiting, 4: Select Supplier
  const [missionCreated, setMissionCreated] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [timerStart, setTimerStart] = useState(null);
  const [supplierResponses, setSupplierResponses] = useState([]);
  const [selectedSuppliersToNotify, setSelectedSuppliersToNotify] = useState([]);
  const [notificationSent, setNotificationSent] = useState(false);
  const pollingRef = useRef(null);

  const [formData, setFormData] = useState({
    customer_id: '',
    carrier_id: '',
    reason_id: '',
    reason_description: '',
    location_lat: '',
    location_lng: '',
    location_address: '',
    vehicle_plate: '',
    vehicle_color: '',
    driver_name: '',
    driver_phone: '',
    observations: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersData, carriersData, reasonsData, suppliersData] = await Promise.all([
          api.get('/customers', token),
          api.get('/carriers', token),
          api.get('/mission-reasons', token),
          api.get('/suppliers', token)
        ]);
        setCustomers(customersData);
        setCarriers(carriersData);
        setReasons(reasonsData);
        setSuppliers(suppliersData.filter(s => s.active));
      } catch (error) {
        toast.error('Erro ao carregar dados');
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, [token]);

  // Poll for prestador responses
  useEffect(() => {
    if (step !== 3 || !missionCreated) return;
    
    const pollResponses = async () => {
      try {
        const data = await api.get(`/missions/${missionCreated.id}/prestador-responses`, token);
        const responses = data.responses || [];
        setSupplierResponses(responses);
        
        // Check if any prestador responded with ETA
        const withEta = responses.filter(r => r.available && r.eta_text);
        if (withEta.length > 0) {
          // Auto-advance to selection when we have at least one response with ETA
          setStep(4);
        }
      } catch (error) {
        console.error('Error polling responses:', error);
      }
    };
    
    // Poll immediately
    pollResponses();
    
    // Then poll every 5 seconds
    pollingRef.current = setInterval(pollResponses, 5000);
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [step, missionCreated, token]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      if (field === 'reason_id') {
        const reason = reasons.find(r => r.id === value);
        if (reason) {
          updated.reason_description = reason.description;
        }
      }
      
      return updated;
    });
  };

  const reverseGeocodeTimer = useRef(null);
  const reverseGeocode = (lat, lng) => {
    if (reverseGeocodeTimer.current) clearTimeout(reverseGeocodeTimer.current);
    reverseGeocodeTimer.current = setTimeout(async () => {
      if (!lat || !lng) return;
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=pt-BR`);
        const data = await res.json();
        if (data.display_name) {
          setFormData(prev => ({ ...prev, location_address: data.display_name }));
        }
      } catch (e) {
        console.log('Geocode error:', e);
      }
    }, 800);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id || !formData.reason_id || !formData.vehicle_plate || !formData.driver_name || !parseFloat(formData.location_lat) || !parseFloat(formData.location_lng)) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setLoading(true);
    try {
      // Send both origin_* and location_* fields so all parts of the system can read them
      const submitData = {
        ...formData,
        origin_lat: parseFloat(formData.location_lat),
        origin_lng: parseFloat(formData.location_lng),
        origin_address: formData.location_address,
        location_lat: parseFloat(formData.location_lat),
        location_lng: parseFloat(formData.location_lng),
      };
      const mission = await api.post('/missions', submitData, token);
      setMissionCreated(mission);
      setStep(2); // Go to supplier selection
      
      // Salvar missão no localStorage para notificação flutuante
      const pendingMissions = JSON.parse(localStorage.getItem('pr_pending_missions') || '[]');
      pendingMissions.push({
        id: mission.id,
        code: mission.code,
        responses: 0,
        createdAt: new Date().toISOString()
      });
      localStorage.setItem('pr_pending_missions', JSON.stringify(pendingMissions));
      
      toast.success('Missão criada! Selecione os fornecedores para notificar.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar missão');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSupplierNotify = (supplierId) => {
    setSelectedSuppliersToNotify(prev => {
      if (prev.includes(supplierId)) {
        return prev.filter(id => id !== supplierId);
      }
      return [...prev, supplierId];
    });
  };

  const handleSelectAllSuppliers = () => {
    if (selectedSuppliersToNotify.length === suppliers.length) {
      setSelectedSuppliersToNotify([]);
    } else {
      setSelectedSuppliersToNotify(suppliers.map(s => s.id));
    }
  };

  const handleNotifySuppliers = async () => {
    if (selectedSuppliersToNotify.length === 0) {
      toast.error('Selecione ao menos um fornecedor');
      return;
    }

    setLoading(true);
    try {
      // Notify prestadores via platform (explicit supplier_ids fixes the notification leak)
      await api.post(`/missions/${missionCreated.id}/notify-prestadores`, { supplier_ids: selectedSuppliersToNotify }, token);

      setNotificationSent(true);
      setTimerStart(new Date().toISOString());
      setStep(3);
      
      toast.success(`Notificação enviada para os fornecedores!`);
    } catch (error) {
      toast.error('Erro ao enviar notificações');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSupplier = async () => {
    if (!selectedSupplier || !missionCreated) {
      toast.error('Selecione um prestador');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/missions/${missionCreated.id}/select-prestador`, {
        prestador_id: selectedSupplier.prestador_id
      }, token);

      toast.success('Prestador selecionado! Demais prestadores foram dispensados.');
      navigate(`/missions/${missionCreated.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao selecionar prestador');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipToSelection = () => {
    // Allow user to manually go to selection even without all responses
    const availableResponses = supplierResponses.filter(r => r.available);
    if (availableResponses.length === 0) {
      toast.error('Nenhum fornecedor respondeu ainda');
      return;
    }
    setStep(4);
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Step 2: Select suppliers to notify via WhatsApp
  if (step === 2) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 pl-14" data-testid="mission-select-notify">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setStep(1)} className="rounded-xl" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Public Sans' }}>
              Selecione os Fornecedores
            </h1>
            <p className="text-slate-500 mt-1">
              Missão <span className="font-mono font-medium">{missionCreated?.code}</span> - Escolha quem será notificado
            </p>
          </div>
        </div>

        <Card className="premium-card border-0">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg" style={{ fontFamily: 'Public Sans' }}>
                Fornecedores Disponíveis
              </CardTitle>
              <Button variant="outline" size="sm" onClick={handleSelectAllSuppliers} className="rounded-lg">
                {selectedSuppliersToNotify.length === suppliers.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
              </Button>
            </div>
            <CardDescription>
              {selectedSuppliersToNotify.length} de {suppliers.length} selecionados
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {suppliers.map((supplier) => {
                const isSelected = selectedSuppliersToNotify.includes(supplier.id);
                return (
                  <div 
                    key={supplier.id} 
                    onClick={() => handleToggleSupplierNotify(supplier.id)}
                    className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all ${
                      isSelected ? 'bg-slate-900 text-white' : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        isSelected ? 'bg-white/20' : 'bg-slate-200'
                      }`}>
                        <Building2 className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-slate-600'}`} />
                      </div>
                      <div>
                        <p className="font-medium">{supplier.name}</p>
                        <p className={`text-sm ${isSelected ? 'text-slate-300' : 'text-slate-500'}`}>
                          📞 {supplier.phone || 'Sem telefone'}
                        </p>
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-white bg-white' : 'border-slate-300'
                    }`}>
                      {isSelected && <CheckCircle className="w-4 h-4 text-slate-900" />}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard')}
            className="flex-1 rounded-xl"
          >
            Voltar para Início
          </Button>
          <Button 
            onClick={handleNotifySuppliers}
            disabled={loading || selectedSuppliersToNotify.length === 0}
            className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl"
            data-testid="send-notification-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Enviar ({selectedSuppliersToNotify.length})
          </Button>
        </div>
      </div>
    );
  }

  // Step 3: Waiting for WhatsApp responses
  if (step === 3) {
    const notifiedSuppliers = suppliers.filter(s => selectedSuppliersToNotify.includes(s.id));
    const availableCount = supplierResponses.filter(r => r.available).length;
    const withEtaCount = supplierResponses.filter(r => r.available && r.eta).length;
    
    return (
      <div className="max-w-2xl mx-auto space-y-6 pl-14" data-testid="mission-waiting">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-xl" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Página Inicial
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Public Sans' }}>
              Aguardando Respostas
            </h1>
            <p className="text-slate-500 mt-1">Código: <span className="font-mono font-medium">{missionCreated?.code}</span></p>
          </div>
        </div>

        <MissionTimer startTime={timerStart} isActive={true} />

        <div className="grid grid-cols-3 gap-4">
          <Card className="premium-card border-0 text-center p-4">
            <p className="text-3xl font-bold text-slate-900">{notifiedSuppliers.length}</p>
            <p className="text-sm text-slate-500">Notificados</p>
          </Card>
          <Card className="premium-card border-0 text-center p-4">
            <p className="text-3xl font-bold text-green-600">{availableCount}</p>
            <p className="text-sm text-slate-500">Disponíveis</p>
          </Card>
          <Card className="premium-card border-0 text-center p-4">
            <p className="text-3xl font-bold text-blue-600">{withEtaCount}</p>
            <p className="text-sm text-slate-500">Com Previsão</p>
          </Card>
        </div>

        <Card className="premium-card border-0">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Public Sans' }}>
              Status das Notificações
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {notifiedSuppliers.map((supplier) => {
                const supplierResponsesForThis = supplierResponses.filter(r => r.supplier_id === supplier.id);
                const response = supplierResponsesForThis.find(r => r.available && r.eta_text) || supplierResponsesForThis.find(r => r.available) || supplierResponsesForThis[0];
                
                let statusIcon, statusBadge, statusColor;
                if (response?.eta_text) {
                  statusIcon = <CheckCircle className="w-5 h-5 text-green-600" />;
                  statusBadge = `${response.prestador_name}: ETA ${formatEta(response.eta_text)}`;
                  statusColor = 'bg-green-100';
                } else if (response?.available) {
                  statusIcon = <Clock className="w-5 h-5 text-blue-600" />;
                  statusBadge = `${response.prestador_name}: Disponível`;
                  statusColor = 'bg-blue-100';
                } else if (response?.available === false) {
                  statusIcon = <AlertTriangle className="w-5 h-5 text-red-600" />;
                  statusBadge = 'Indisponível';
                  statusColor = 'bg-red-100';
                } else {
                  statusIcon = <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />;
                  statusBadge = 'Aguardando resposta';
                  statusColor = 'bg-slate-100';
                }
                
                return (
                  <div key={supplier.id} className={`flex items-center justify-between p-4 rounded-xl ${statusColor}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                        {statusIcon}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{supplier.name}</p>
                        <p className="text-sm text-slate-500">{supplier.phone}</p>
                      </div>
                    </div>
                    <Badge className="bg-white text-slate-700 border border-slate-200 rounded-full">
                      {statusBadge}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {availableCount > 0 && (
          <Button 
            onClick={handleSkipToSelection}
            className="w-full bg-slate-900 hover:bg-slate-800 rounded-xl"
          >
            Selecionar Fornecedor ({availableCount} disponíveis)
          </Button>
        )}
      </div>
    );
  }

  // Step 4: Select prestador from responses
  if (step === 4) {
    // Filter only available prestadores, best availability first (fastest response first)
    const availableResponses = supplierResponses.filter(r => r.available);
    const sortedResponses = [...availableResponses].sort((a, b) => {
      // Sort by those with ETA first, then by fastest response time
      if (a.eta_text && !b.eta_text) return -1;
      if (!a.eta_text && b.eta_text) return 1;
      return new Date(a.responded_at) - new Date(b.responded_at);
    });

    const firstWithEta = sortedResponses[0];
    
    return (
      <div className="max-w-2xl mx-auto space-y-6 pl-14" data-testid="mission-select-supplier">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setStep(3)} className="rounded-xl" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Public Sans' }}>
              Selecionar Prestador
            </h1>
            <p className="text-slate-500 mt-1">Escolha o prestador para a missão <span className="font-mono font-medium">{missionCreated?.code}</span></p>
          </div>
        </div>

        {firstWithEta && (
          <Card className="premium-card border-0 bg-gradient-to-br from-green-600 to-green-700 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Zap className="w-7 h-7" />
                </div>
                <div>
                  <p className="text-green-100 text-sm">Melhor disponibilidade</p>
                  <p className="text-2xl font-semibold">{firstWithEta.prestador_name}</p>
                  <p className="text-green-200 text-sm">{firstWithEta.supplier_name}{firstWithEta.eta_text ? ` • ETA: ${formatEta(firstWithEta.eta_text)}` : ''}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="premium-card border-0">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Public Sans' }}>
              Prestadores Disponíveis ({availableResponses.length})
            </CardTitle>
            <CardDescription>
              Selecione o prestador desejado
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sortedResponses.map((response) => {
                const supplier = suppliers.find(s => s.id === response.supplier_id) || {};
                const isSelected = selectedSupplier?.prestador_id === response.prestador_id;
                
                return (
                  <div 
                    key={response.prestador_id}
                    onClick={() => setSelectedSupplier(response)}
                    className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      isSelected 
                        ? 'border-slate-900 bg-slate-50 shadow-lg' 
                        : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                    }`}
                    data-testid={`prestador-response-${response.prestador_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSelected ? 'bg-slate-900 text-white' : 'bg-slate-100'}`}>
                          <Building2 className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{response.prestador_name}</p>
                          <p className="text-sm text-slate-500">{response.supplier_name}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                            <span className="text-xs font-medium text-slate-600">{(response.rating_avg || 0).toFixed(1)}</span>
                            <span className="text-xs text-slate-400">({response.rating_count || 0})</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        {response.eta_text ? (
                          <>
                            <p className="text-sm text-slate-400">Previsão de chegada</p>
                            <p className="font-semibold text-green-600" style={{ fontFamily: 'JetBrains Mono' }}>
                              {formatEta(response.eta_text)}
                            </p>
                          </>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-600">Sem ETA</Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                      {supplier.pricing?.[0]?.base_price && (
                        <span className="text-sm text-slate-500">
                          Base: <span className="font-mono font-medium text-slate-700">R$ {supplier.pricing[0].base_price}</span>
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard')}
            className="rounded-xl"
          >
            Voltar para Início
          </Button>
          <Button 
            onClick={handleSelectSupplier}
            className="bg-slate-900 hover:bg-slate-800 rounded-xl btn-premium"
            disabled={!selectedSupplier || loading}
            data-testid="confirm-supplier-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Confirmar Fornecedor
          </Button>
        </div>
      </div>
    );
  }

  // Step 1: Form
  return (
    <div className="max-w-4xl mx-auto space-y-6 pl-14" data-testid="new-mission-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-xl" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Public Sans' }}>
            Solicitar Missão
          </h1>
          <p className="text-slate-500 mt-1">Preencha os dados para criar uma nova solicitação</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Alert */}
        <Card className="premium-card border-0 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <CardContent className="p-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6" />
            </div>
            <div>
              <p className="font-semibold text-lg">Pronta Resposta</p>
              <p className="text-slate-300 mt-1">
                Na próxima etapa você escolhe os fornecedores a notificar. Os prestadores
                disponíveis irão responder e você seleciona quem irá atender.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Customer & Reason */}
        <Card className="premium-card border-0">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
              <AlertTriangle className="w-5 h-5 text-slate-400" />
              Ocorrência
            </CardTitle>
            <CardDescription>Selecione o cliente e o motivo da ocorrência</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customer">Cliente *</Label>
              <Select value={formData.customer_id} onValueChange={(v) => handleChange('customer_id', v)}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="customer-select">
                  <SelectValue placeholder="Selecione o cliente" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {customers.filter(c => c.active).map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="carrier">Transportador (opcional)</Label>
              <Select value={formData.carrier_id || "none"} onValueChange={(v) => handleChange('carrier_id', v === "none" ? "" : v)}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="carrier-select">
                  <SelectValue placeholder="Selecione o transportador" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="none">Nenhum</SelectItem>
                  {carriers.filter(c => c.active).map(carrier => (
                    <SelectItem key={carrier.id} value={carrier.id}>{carrier.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Motivo *</Label>
              <Select value={formData.reason_id} onValueChange={(v) => handleChange('reason_id', v)}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="reason-select">
                  <SelectValue placeholder="Selecione o motivo" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {reasons.filter(r => r.active).map(reason => (
                    <SelectItem key={reason.id} value={reason.id}>
                      <span className="font-mono mr-2">{reason.code}</span>
                      {reason.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason_description">Descrição do Motivo</Label>
              <Input
                id="reason_description"
                value={formData.reason_description}
                onChange={(e) => handleChange('reason_description', e.target.value)}
                placeholder="Descrição automática ou personalizada"
                className="h-12 rounded-xl"
                data-testid="reason-description-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Location */}
        <Card className="premium-card border-0">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
              <MapPin className="w-5 h-5 text-slate-400" />
              Localização
            </CardTitle>
            <CardDescription>Informe as coordenadas do local da ocorrência</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location_lat">Latitude *</Label>
                <Input
                  id="location_lat"
                  type="text"
                  inputMode="decimal"
                  value={formData.location_lat}
                  onChange={(e) => {
                    const raw = e.target.value;
                    handleChange('location_lat', raw);
                    const lat = parseFloat(raw);
                    const lng = parseFloat(formData.location_lng);
                    if (!isNaN(lat) && !isNaN(lng)) reverseGeocode(lat, lng);
                  }}
                  placeholder="-23.550520"
                  className="h-12 rounded-xl font-mono"
                  data-testid="lat-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location_lng">Longitude *</Label>
                <Input
                  id="location_lng"
                  type="text"
                  inputMode="decimal"
                  value={formData.location_lng}
                  onChange={(e) => {
                    const raw = e.target.value;
                    handleChange('location_lng', raw);
                    const lat = parseFloat(formData.location_lat);
                    const lng = parseFloat(raw);
                    if (!isNaN(lat) && !isNaN(lng)) reverseGeocode(lat, lng);
                  }}
                  placeholder="-46.633308"
                  className="h-12 rounded-xl font-mono"
                  data-testid="lng-input"
                />
              </div>
            </div>

            {formData.location_address && (
              <div className="p-3 rounded-xl bg-green-50 border border-green-200">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-800">{formData.location_address}</p>
                    <a
                      href={`https://www.google.com/maps?q=${parseFloat(formData.location_lat)},${parseFloat(formData.location_lng)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-green-600 hover:underline mt-1 inline-block"
                      data-testid="google-maps-link"
                    >
                      Abrir no Google Maps
                    </a>
                  </div>
                </div>
              </div>
            )}

            {!formData.location_address && formData.location_lat && formData.location_lng && (
              <div className="p-3 rounded-xl bg-slate-50 border">
                <p className="text-xs text-slate-500">Preencha latitude e longitude para buscar o endereço automaticamente</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="location_observations">Observações do Endereço</Label>
              <Input
                id="location_observations"
                value={formData.observations}
                onChange={(e) => handleChange('observations', e.target.value)}
                placeholder="Ex: Próximo ao posto Shell, faixa direita, km 50"
                className="h-12 rounded-xl"
                data-testid="location-observations-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Vehicle */}
        <Card className="premium-card border-0">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
              <Truck className="w-5 h-5 text-slate-400" />
              Veículo
            </CardTitle>
            <CardDescription>Dados do veículo envolvido</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="vehicle_plate">Placa *</Label>
              <Input
                id="vehicle_plate"
                value={formData.vehicle_plate}
                onChange={(e) => handleChange('vehicle_plate', e.target.value.toUpperCase())}
                placeholder="ABC-1234"
                className="h-12 rounded-xl font-mono"
                data-testid="plate-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vehicle_color">Cor</Label>
              <Input
                id="vehicle_color"
                value={formData.vehicle_color}
                onChange={(e) => handleChange('vehicle_color', e.target.value)}
                placeholder="Branco"
                className="h-12 rounded-xl"
                data-testid="color-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Driver */}
        <Card className="premium-card border-0">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
              <User className="w-5 h-5 text-slate-400" />
              Motorista
            </CardTitle>
            <CardDescription>Dados para contato</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="driver_name">Nome *</Label>
              <Input
                id="driver_name"
                value={formData.driver_name}
                onChange={(e) => handleChange('driver_name', e.target.value)}
                placeholder="Nome do motorista"
                className="h-12 rounded-xl"
                data-testid="driver-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="driver_phone">Telefone *</Label>
              <Input
                id="driver_phone"
                value={formData.driver_phone}
                onChange={(e) => handleChange('driver_phone', e.target.value)}
                placeholder="(11) 99999-9999"
                className="h-12 rounded-xl"
                data-testid="driver-phone-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Observations */}
        <Card className="premium-card border-0">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Public Sans' }}>
              Observações
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.observations}
              onChange={(e) => handleChange('observations', e.target.value)}
              placeholder="Informações adicionais sobre a ocorrência..."
              rows={4}
              className="rounded-xl resize-none"
              data-testid="observations-input"
            />
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="rounded-xl h-12 px-6" data-testid="cancel-btn">
            Cancelar
          </Button>
          <Button 
            type="submit" 
            className="bg-slate-900 hover:bg-slate-800 rounded-xl h-12 px-8 btn-premium"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
            Enviar Solicitação
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewMissionPage;
